import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { clearAccessToken } from '../api/api';
import { clearProfileCache } from '../api/profileApi';
import {
  getCalendarSetup,
  saveCalendarSetup,
  listGoogleCalendars,
  saveGoogleCalendars,
  listMicrosoftCalendars,
  saveMicrosoftCalendars,
  connectAppleCalendar,
  listAppleCalendars,
  saveAppleCalendars,
} from '../api/calendarApi';
 
const CalendarSetup = () => {
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [masterCalendars, setMasterCalendars] = useState([]);
  const [selectedMasterCalendar, setSelectedMasterCalendar] = useState('');
  const [savedMasterCalendar, setSavedMasterCalendar] = useState('');
  const [selectedProviderType, setSelectedProviderType] = useState('');
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [masterError, setMasterError] = useState('');
  const [masterSaved, setMasterSaved] = useState(false);
 
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [selectedGoogleId, setSelectedGoogleId] = useState('');
  const [savedGoogleId, setSavedGoogleId] = useState('');
  const [microsoftCalendars, setMicrosoftCalendars] = useState([]);
  const [selectedMicrosoftId, setSelectedMicrosoftId] = useState('');
  const [savedMicrosoftId, setSavedMicrosoftId] = useState('');
  const [googleConnected, setGoogleConnected] = useState(false);
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [appleCalendars, setAppleCalendars] = useState([]);
  const [selectedAppleId, setSelectedAppleId] = useState('');
  const [savedAppleId, setSavedAppleId] = useState('');
  const [appleConnected, setAppleConnected] = useState(false);
  const [appleEmail, setAppleEmail] = useState('');
  const [appleAppPassword, setAppleAppPassword] = useState('');
  const [showAppleForm, setShowAppleForm] = useState(false);
 
  const [syncLoading, setSyncLoading] = useState(false);
  const [savingGoogle, setSavingGoogle] = useState(false);
  const [savingMicrosoft, setSavingMicrosoft] = useState(false);
  const [savingApple, setSavingApple] = useState(false);
  const [savingMaster, setSavingMaster] = useState(false);
  const [error, setError] = useState('');
  const [calendarsSaved, setCalendarsSaved] = useState(false);
  const [eventsSynced, setEventsSynced] = useState(false);
  const [syncSuccessMessage, setSyncSuccessMessage] = useState('');
  const [syncJobStatus, setSyncJobStatus] = useState('idle');
  const [syncProcessedCount, setSyncProcessedCount] = useState(0);
  const [syncProviderLabel, setSyncProviderLabel] = useState('');
 
  const [manualFile, setManualFile] = useState(null);
  const [manualUploading, setManualUploading] = useState(false);
  const [manualUploaded, setManualUploaded] = useState(false);
 
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getConnectErrorMessage = (err, providerLabel) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    const serverMsg = data.error || data.message;
    const lowerServerMsg = String(serverMsg || '').toLowerCase();

    if (status === 401) {
      if (
        serverMsg === 'APPLE_CREDENTIALS_INVALID' ||
        lowerServerMsg.includes('apple') ||
        lowerServerMsg.includes('credential') ||
        lowerServerMsg.includes('app-specific')
      ) {
        return 'Apple credentials are incorrect. Please use the correct iCloud email and app-specific password.';
      }
      return 'Your session ended. Please sign in again and try once more.';
    }

    if (status === 408) {
      return 'This is taking longer than expected. Please try again.';
    }

    // Axios timeout / network style failures
    if (err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''))) {
      return 'Connection is taking too long right now. Please try again in a moment.';
    }

    if (!status) {
      return 'We could not reach the server. Please check your internet and try again.';
    }

    if (lowerServerMsg.includes('does not match your signed-in account')) {
      return 'Please use the same account you signed in with.';
    }

    if (lowerServerMsg.includes('already linked to another user')) {
      return 'This calendar account is already connected to another profile. Please use a different account.';
    }

    return 'We could not complete the connection. Please try again.';
  };

  const inferProviderTypeFromName = (name) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('google')) return 'google';
    if (n.includes('microsoft')) return 'microsoft';
    if (n.includes('apple') || n.includes('icloud')) return 'apple';
    if (n.includes('manual')) return 'manual';
    return '';
  };

  const getSyncErrorMessage = (err, providerLabel) => {
    const status = err?.response?.status;
    const data = err?.response?.data || {};
    const serverMsg = data.error || data.message || err?.message || '';
    const lowerMsg = String(serverMsg).toLowerCase();

    if (status === 401) {
      return 'Your session ended. Please sign in again and try once more.';
    }

    if (status === 408 || /timed out|timeout/i.test(String(err?.message || ''))) {
      return 'Sync is taking longer than expected. Please try again in a moment.';
    }

    if (err?.code === 'ECONNABORTED') {
      return 'Sync is taking longer than expected. Please try again in a moment.';
    }

    if (!status) {
      return 'We could not reach the server. Please check your internet and try again.';
    }

    if (status === 409 || lowerMsg.includes('not connected') || lowerMsg.includes('reconnect')) {
      return `Please reconnect your ${providerLabel} account and try again.`;
    }

    if (lowerMsg.includes('not selected') || lowerMsg.includes('calendar')) {
      return 'Please select a calendar first, then try syncing again.';
    }

    if (lowerMsg.includes('still running') || lowerMsg.includes('in progress')) {
      return 'A sync is already running. Please wait a moment and refresh.';
    }

    return 'We could not sync events right now. Please try again.';
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {}
    clearAccessToken();
    clearProfileCache();
    localStorage.removeItem('hasProfessionalProfile');
    sessionStorage.clear();
    navigate('/login', { replace: true });
  };
 
  const loadConnectionStatus = async () => {
    try {
      const res = await api.get('/calendar/setup-calendar');
      const data = res.data || {};
      setGoogleConnected(!!data.googleConnected);
      setMicrosoftConnected(!!data.microsoftConnected);
      setAppleConnected(!!data.appleConnected);
    } catch (e) {
      console.error('[CalendarSetup] Failed to load connection status', e);
    }
  };
 
  const loadMasterCalendars = async () => {
    setLoadingMaster(true);
    setMasterError('');
    try {
      const res = await getCalendarSetup();
      const data = res.data || {};
      const options = data.options || {};
      const selected = data.selected || {};
      const list = options.calendars || [];
      setMasterCalendars(list);
      const uid = selected.calendar_uid || '';
      setSelectedMasterCalendar(uid);
      setSavedMasterCalendar(uid);
      let pType = selected.provider_type || '';
      if (!pType && uid) {
        const found = list.find((c) => c.uid === uid);
        if (found) pType = found.provider_type || inferProviderTypeFromName(found.name);
      }
      setSelectedProviderType(pType || '');
      if (pType) {
        localStorage.removeItem('pending_calendar_provider');
      }
      setMasterSaved(!!uid);
    } catch (e) {
      console.error('[CalendarSetup] Failed to load master calendars', e);
      setMasterError('Failed to load calendars from server.');
      setMasterCalendars([]);
      setSelectedMasterCalendar('');
      setSelectedProviderType('');
      setMasterSaved(false);
    } finally {
      setLoadingMaster(false);
    }
  };
 
  const loadGoogleCalendars = async () => {
    setError('');
    if (!googleConnected) {
      setGoogleCalendars([]);
      setSelectedGoogleId('');
      return;
    }
    try {
      const res = await listGoogleCalendars();
      const data = res.data || {};
      const items = data.calendars || [];
      setGoogleCalendars(
        items.map((c) => ({
          id: c.id,
          name: c.summary || c.id,
          primary: !!c.primary,
          selected: !!c.selected,
        }))
      );
      const firstSelected = items.find((c) => c.selected);
      const selectedId = firstSelected ? firstSelected.id : '';
      setSelectedGoogleId(selectedId);
      setSavedGoogleId(selectedId);
      setCalendarsSaved(!!selectedId);
    } catch (e) {
      const status = e?.response?.status;
      console.error('[CalendarSetup] Failed to load Google calendars', e);
      if (status === 409) {
        setError('');
      } else {
        setError('Failed to load Google calendars. Please connect and try again.');
        toast.error('Failed to load Google calendars. Please connect and try again.');
      }
      setGoogleCalendars([]);
      setSelectedGoogleId('');
    }
  };
 
  const loadMicrosoftCalendars = async () => {
    setError('');
    if (!microsoftConnected) {
      setMicrosoftCalendars([]);
      setSelectedMicrosoftId('');
      return;
    }
    try {
      const res = await listMicrosoftCalendars();
      const data = res.data || {};
      const items = data.calendars || [];
      setMicrosoftCalendars(
        items.map((c) => ({
          id: c.id,
          name: c.name || c.id,
          primary: !!c.isDefault,
          selected: !!c.selected,
        }))
      );
      const firstSelected = items.find((c) => c.selected);
      const selectedId = firstSelected ? firstSelected.id : '';
      setSelectedMicrosoftId(selectedId);
      setSavedMicrosoftId(selectedId);
      setCalendarsSaved(!!selectedId);
    } catch (e) {
      const status = e?.response?.status;
      console.error('[CalendarSetup] Failed to load Microsoft calendars', e);
      if (status === 409) {
        setError('');
      } else {
        setError('Failed to load Microsoft calendars. Please connect and try again.');
        toast.error('Failed to load Microsoft calendars. Please connect and try again.');
      }
      setMicrosoftCalendars([]);
      setSelectedMicrosoftId('');
    }
  };

  const loadAppleCalendars = async () => {
    setError('');
    if (!appleConnected) {
      setAppleCalendars([]);
      setSelectedAppleId('');
      return;
    }
    try {
      const res = await listAppleCalendars();
      const data = res.data || {};
      const items = data.calendars || [];
      setAppleCalendars(
        items.map((c) => ({
          id: c.id,
          name: c.name || c.id,
          selected: !!c.selected,
        }))
      );
      const firstSelected = items.find((c) => c.selected);
      const selectedId = firstSelected ? firstSelected.id : '';
      setSelectedAppleId(selectedId);
      setSavedAppleId(selectedId);
      setCalendarsSaved(!!selectedId);
    } catch (e) {
      const status = e?.response?.status;
      console.error('[CalendarSetup] Failed to load Apple calendars', e);
      if (status === 409) {
        setError('');
      } else {
        setError('Failed to load Apple calendars. Please connect and try again.');
        toast.error('Failed to load Apple calendars. Please connect and try again.');
      }
      setAppleCalendars([]);
      setSelectedAppleId('');
    }
  };
 
  useEffect(() => {
    loadMasterCalendars();
    loadConnectionStatus();
  }, []);
 
  useEffect(() => {
    if (!masterSaved || selectedProviderType === 'manual') {
      setError('');
    }
  }, [masterSaved, selectedProviderType]);
 
  useEffect(() => {
    const provider = searchParams.get('provider') || localStorage.getItem('pending_calendar_provider');
    if (provider === 'google' || provider === 'microsoft' || provider === 'apple') {
      setSelectedProviderType(provider);
      localStorage.removeItem('pending_calendar_provider');
    }
  }, [searchParams]);
 
  useEffect(() => {
    // when provider type changes, clear step 3/4 state
    setCalendarsSaved(false);
    setEventsSynced(false);
    setSyncSuccessMessage('');
    if (!masterSaved) {
      setGoogleCalendars([]);
      setMicrosoftCalendars([]);
      setAppleCalendars([]);
      setSelectedGoogleId('');
      setSavedGoogleId('');
      setSelectedMicrosoftId('');
      setSavedMicrosoftId('');
      setSelectedAppleId('');
      setSavedAppleId('');
      return;
    }
    if (selectedProviderType === 'google' && googleConnected) {
      loadGoogleCalendars();
    } else if (selectedProviderType === 'microsoft' && microsoftConnected) {
      loadMicrosoftCalendars();
    } else if (selectedProviderType === 'apple' && appleConnected) {
      loadAppleCalendars();
    } else {
      setGoogleCalendars([]);
      setMicrosoftCalendars([]);
      setAppleCalendars([]);
      setSelectedGoogleId('');
      setSelectedMicrosoftId('');
      setSelectedAppleId('');
    }
  }, [selectedProviderType, googleConnected, microsoftConnected, appleConnected, masterSaved]);
 
  const handleMasterChange = (e) => {
    const uid = e.target.value;
    setSelectedMasterCalendar(uid);
    setCalendarsSaved(false);
    setEventsSynced(false);
    setSyncSuccessMessage('');
    setManualFile(null);
    setManualUploaded(false);
    if (!uid) {
      setSelectedProviderType('');
      setMasterSaved(false);
      return;
    }
    const found = masterCalendars.find((c) => c.uid === uid);
    const nextType = (found && found.provider_type) || inferProviderTypeFromName(found?.name);
    setSelectedProviderType(nextType || '');
    // If user re-selects already saved master calendar, keep setup progressed.
    setMasterSaved(uid === savedMasterCalendar);
  };
 
  const handleSaveMasterCalendar = async () => {
    if (!selectedMasterCalendar) {
      toast.warn('Please choose a calendar.');
      return;
    }
    setSavingMaster(true);
    try {
      const selectedEntry = masterCalendars.find((c) => c.uid === selectedMasterCalendar);
      const detectedProvider =
        selectedProviderType ||
        selectedEntry?.provider_type ||
        inferProviderTypeFromName(selectedEntry?.name);
      // Enforce single-provider connection:
      // if master provider changes, disconnect previously connected provider(s) automatically.
      if (selectedProviderType === 'google' && microsoftConnected) {
        await api.post('/calendar/disconnect/microsoft');
        setMicrosoftConnected(false);
        setMicrosoftCalendars([]);
        setSelectedMicrosoftId('');
        setSavedMicrosoftId('');
      } else if (selectedProviderType === 'google' && appleConnected) {
        await api.post('/calendar/disconnect/apple');
        setAppleConnected(false);
        setAppleCalendars([]);
        setSelectedAppleId('');
        setSavedAppleId('');
      } else if (selectedProviderType === 'microsoft' && googleConnected) {
        await api.post('/calendar/disconnect/google');
        setGoogleConnected(false);
        setGoogleCalendars([]);
        setSelectedGoogleId('');
        setSavedGoogleId('');
      } else if (selectedProviderType === 'microsoft' && appleConnected) {
        await api.post('/calendar/disconnect/apple');
        setAppleConnected(false);
        setAppleCalendars([]);
        setSelectedAppleId('');
        setSavedAppleId('');
      } else if (selectedProviderType === 'apple' && (googleConnected || microsoftConnected)) {
        await api.post('/calendar/disconnect/all');
        setGoogleConnected(false);
        setMicrosoftConnected(false);
        setGoogleCalendars([]);
        setMicrosoftCalendars([]);
        setSelectedGoogleId('');
        setSavedGoogleId('');
        setSelectedMicrosoftId('');
        setSavedMicrosoftId('');
      } else if (selectedProviderType === 'manual' && (googleConnected || microsoftConnected)) {
        await api.post('/calendar/disconnect/all');
        setGoogleConnected(false);
        setMicrosoftConnected(false);
        setGoogleCalendars([]);
        setMicrosoftCalendars([]);
        setSelectedGoogleId('');
        setSavedGoogleId('');
        setSelectedMicrosoftId('');
        setSavedMicrosoftId('');
        setAppleConnected(false);
        setAppleCalendars([]);
        setSelectedAppleId('');
        setSavedAppleId('');
      } else if (selectedProviderType === 'manual' && appleConnected) {
        await api.post('/calendar/disconnect/apple');
        setAppleConnected(false);
        setAppleCalendars([]);
        setSelectedAppleId('');
        setSavedAppleId('');
      }

      await saveCalendarSetup(selectedMasterCalendar);
      setMasterSaved(true);
      setSavedMasterCalendar(selectedMasterCalendar);
      setCalendarsSaved(false);
      setEventsSynced(false);
      setSyncSuccessMessage('');
      if (!selectedProviderType && detectedProvider) {
        setSelectedProviderType(detectedProvider);
      }
      if (detectedProvider) {
        localStorage.removeItem('pending_calendar_provider');
      }
      toast.success('Calendar saved successfully.');
    } catch (e) {
      console.error('[CalendarSetup] Failed to save master calendar', e);
      toast.error('Failed to save calendar.');
    } finally {
      setSavingMaster(false);
    }
  };
 
  const handleGoogleConnect = async () => {
    try {
      setSyncLoading(true);
      setError('');
      const res = await api.post('/calendar/connection/google');
      console.debug('Google connection result', res.data);
      setGoogleConnected(true);
      await loadGoogleCalendars();
      toast.success('Google calendar account connected.');
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      const msg = data.error || 'Google connection failed';
      if (status === 409 || msg === 'Google calendar not connected') {
        try {
          localStorage.setItem('pending_calendar_provider', 'google');
          const startRes = await api.get('/oauth/google/calendar/start', {
            params: { calendar_uid: selectedMasterCalendar },
          });
          const url = startRes.data?.auth_url;
          if (url) {
            window.location.href = url;
          } else {
            toast.error('Failed to start Google calendar connection.');
          }
        } catch (startErr) {
          console.error('Failed to start Google calendar OAuth', startErr);
          toast.error('Failed to start Google calendar connection.');
        }
        return;
      }
      const uiMsg = getConnectErrorMessage(err, 'calendar');
      setError(uiMsg);
      toast.error(uiMsg);
    } finally {
      setSyncLoading(false);
    }
  };
 
  const handleMicrosoftConnect = async () => {
    try {
      setSyncLoading(true);
      setError('');
      const res = await api.post('/calendar/connection/microsoft');
      console.debug('Microsoft connection result', res.data);
      setMicrosoftConnected(true);
      await loadMicrosoftCalendars();
      toast.success('Microsoft calendar account connected.');
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data || {};
      const msg = data.error || 'Microsoft connection failed';
      if (status === 409 || msg === 'Microsoft calendar not connected') {
        try {
          localStorage.setItem('pending_calendar_provider', 'microsoft');
          const startRes = await api.get('/oauth/microsoft/calendar/start', {
            params: { calendar_uid: selectedMasterCalendar },
          });
          const url = startRes.data?.auth_url;
          if (url) {
            window.location.href = url;
          } else {
            toast.error('Failed to start Microsoft calendar connection.');
          }
        } catch (startErr) {
          console.error('Failed to start Microsoft calendar OAuth', startErr);
          toast.error('Failed to start Microsoft calendar connection.');
        }
        return;
      }
      const uiMsg = getConnectErrorMessage(err, 'calendar');
      setError(uiMsg);
      toast.error(uiMsg);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleAppleConnect = async () => {
    const email = String(appleEmail || '').trim();
    const pwd = String(appleAppPassword || '').trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !pwd) {
      toast.warn('Please enter your iCloud email and app-specific password.');
      return;
    }
    if (!emailPattern.test(email)) {
      toast.warn('Please enter a valid email address.');
      return;
    }
    try {
      setSyncLoading(true);
      setSavingApple(true);
      setError('');
      await connectAppleCalendar({
        email,
        app_password: pwd,
      });
      setAppleConnected(true);
      setShowAppleForm(false);
      setAppleAppPassword('');
      await loadAppleCalendars();
      toast.success('Apple calendar connected.');
    } catch (err) {
      const uiMsg = getConnectErrorMessage(err, 'calendar');
      setError(uiMsg);
      toast.error(uiMsg);
    } finally {
      setSyncLoading(false);
      setSavingApple(false);
    }
  };
 
  const toggleGoogleSelection = (id) => {
    setSelectedGoogleId(id);
    setCalendarsSaved(id === savedGoogleId);
    setEventsSynced(false);
    setSyncSuccessMessage('');
  };
 
  const toggleMicrosoftSelection = (id) => {
    setSelectedMicrosoftId(id);
    setCalendarsSaved(id === savedMicrosoftId);
    setEventsSynced(false);
    setSyncSuccessMessage('');
  };

  const toggleAppleSelection = (id) => {
    setSelectedAppleId(id);
    setCalendarsSaved(id === savedAppleId);
    setEventsSynced(false);
    setSyncSuccessMessage('');
  };
 
  const handleSaveGoogleCalendars = async () => {
    if (!selectedGoogleId) {
      toast.warn('Please select a Google calendar to sync.');
      return;
    }
    setSavingGoogle(true);
    setError('');
    try {
      await saveGoogleCalendars([selectedGoogleId]);
      const googleMaster = masterCalendars.find((c) => {
        const p = (c.provider_type || '').toLowerCase();
        const n = (c.name || '').toLowerCase();
        return p === 'google' || n.includes('google');
      });
      if (googleMaster) {
        setSelectedMasterCalendar(googleMaster.uid);
        setSelectedProviderType('google');
        try {
          await saveCalendarSetup(googleMaster.uid);
          setMasterSaved(true);
        } catch (e) {
          console.error('[CalendarSetup] Failed to save google master calendar pointer', e);
        }
      }
      setCalendarsSaved(true);
      setEventsSynced(false);
      setSyncSuccessMessage('');
      setSavedGoogleId(selectedGoogleId);
      toast.success('Google calendar selection saved successfully.');
    } catch (e) {
      console.error('[CalendarSetup] Failed to save Google calendars', e);
      setError('Failed to save Google calendars.');
      toast.error('Failed to save Google calendars.');
    } finally {
      setSavingGoogle(false);
    }
  };
 
  const handleSaveMicrosoftCalendars = async () => {
    if (!selectedMicrosoftId) {
      toast.warn('Please select a Microsoft calendar to sync.');
      return;
    }
    setSavingMicrosoft(true);
    setError('');
    try {
      await saveMicrosoftCalendars([selectedMicrosoftId]);
      const msMaster = masterCalendars.find((c) => {
        const p = (c.provider_type || '').toLowerCase();
        const n = (c.name || '').toLowerCase();
        return p === 'microsoft' || n.includes('microsoft');
      });
      if (msMaster) {
        setSelectedMasterCalendar(msMaster.uid);
        setSelectedProviderType('microsoft');
        try {
          await saveCalendarSetup(msMaster.uid);
          setMasterSaved(true);
        } catch (e) {
          console.error('[CalendarSetup] Failed to save microsoft master calendar pointer', e);
        }
      }
      setCalendarsSaved(true);
      setEventsSynced(false);
      setSyncSuccessMessage('');
      setSavedMicrosoftId(selectedMicrosoftId);
      toast.success('Microsoft calendar selection saved successfully.');
    } catch (e) {
      console.error('[CalendarSetup] Failed to save Microsoft calendars', e);
      setError('Failed to save Microsoft calendars.');
      toast.error('Failed to save Microsoft calendars.');
    } finally {
      setSavingMicrosoft(false);
    }
  };

  const handleSaveAppleCalendars = async () => {
    if (!selectedAppleId) {
      toast.warn('Please select an Apple calendar to sync.');
      return;
    }
    setSavingApple(true);
    setError('');
    try {
      await saveAppleCalendars([selectedAppleId]);
      const appleMaster = masterCalendars.find((c) => {
        const p = (c.provider_type || '').toLowerCase();
        const n = (c.name || '').toLowerCase();
        return p === 'apple' || n.includes('apple') || n.includes('icloud');
      });
      if (appleMaster) {
        setSelectedMasterCalendar(appleMaster.uid);
        setSelectedProviderType('apple');
        try {
          await saveCalendarSetup(appleMaster.uid);
          setMasterSaved(true);
        } catch (e) {
          console.error('[CalendarSetup] Failed to save apple master calendar pointer', e);
        }
      }
      setCalendarsSaved(true);
      setEventsSynced(false);
      setSyncSuccessMessage('');
      setSavedAppleId(selectedAppleId);
      toast.success('Apple calendar selection saved successfully.');
    } catch (e) {
      console.error('[CalendarSetup] Failed to save Apple calendars', e);
      setError('Failed to save Apple calendars.');
      toast.error('Failed to save Apple calendars.');
    } finally {
      setSavingApple(false);
    }
  };
 
  const handleGoogleSyncEvents = async () => {
    try {
      setSyncLoading(true);
      setSyncJobStatus('running');
      setSyncProviderLabel('Google');
      setSyncProcessedCount(0);
      setError('');
      setSyncSuccessMessage('');
      const res = await api.post('/calendar/sync-events/google');
      const jobId = res?.data?.job_id;
      if (!jobId) {
        throw new Error('Sync job was not created');
      }
      sessionStorage.setItem('calendar_sync_provider', 'google');
      sessionStorage.setItem('calendar_sync_job_id', jobId);
      setEventsSynced(true);
      setSyncSuccessMessage('Google sync started. You can continue to dashboard.');
    } catch (err) {
      toast.error(getSyncErrorMessage(err, 'Google'));
    } finally {
      setSyncLoading(false);
    }
  };
 
  const handleMicrosoftSyncEvents = async () => {
    try {
      setSyncLoading(true);
      setSyncJobStatus('running');
      setSyncProviderLabel('Microsoft');
      setSyncProcessedCount(0);
      setError('');
      setSyncSuccessMessage('');
      const res = await api.post('/calendar/sync-events/microsoft');
      const jobId = res?.data?.job_id;
      if (!jobId) {
        throw new Error('Sync job was not created');
      }
      sessionStorage.setItem('calendar_sync_provider', 'microsoft');
      sessionStorage.setItem('calendar_sync_job_id', jobId);
      setEventsSynced(true);
      setSyncSuccessMessage('Microsoft sync started. You can continue to dashboard.');
    } catch (err) {
      toast.error(getSyncErrorMessage(err, 'Microsoft'));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleAppleSyncEvents = async () => {
    try {
      setSyncLoading(true);
      setSyncJobStatus('running');
      setSyncProviderLabel('Apple');
      setSyncProcessedCount(0);
      setError('');
      setSyncSuccessMessage('');
      const res = await api.post('/calendar/sync-events/apple');
      const jobId = res?.data?.job_id;
      if (!jobId) {
        throw new Error('Sync job was not created');
      }
      sessionStorage.setItem('calendar_sync_provider', 'apple');
      sessionStorage.setItem('calendar_sync_job_id', jobId);
      setEventsSynced(true);
      setSyncSuccessMessage('Apple sync started. You can continue to dashboard.');
    } catch (err) {
      toast.error(getSyncErrorMessage(err, 'Apple'));
    } finally {
      setSyncLoading(false);
    }
  };
 
  const handleManualFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setManualFile(file || null);
    setManualUploaded(false);
    setEventsSynced(false);
    setSyncSuccessMessage('');
  };
 
  const handleManualUpload = async () => {
    if (!manualFile) {
      toast.warn('Please choose a .ics file to upload.');
      return;
    }
    setManualUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', manualFile);
      await api.post('/event/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setManualUploaded(true);
      toast.success('Calendar file uploaded successfully.');
    } catch (e) {
      console.error('[CalendarSetup] Failed to upload calendar file', e);
      toast.error('Failed to upload calendar file.');
    } finally {
      setManualUploading(false);
    }
  };
 
  const getSelectedMasterLabel = () => {
    if (!selectedMasterCalendar) return '';
    const found = masterCalendars.find((c) => c.uid === selectedMasterCalendar);
    if (!found) return '';
    let providerLabel = '';
    if (selectedProviderType === 'google') providerLabel = 'Google';
    else if (selectedProviderType === 'microsoft') providerLabel = 'Microsoft';
    else if (selectedProviderType === 'apple') providerLabel = 'Apple';
    else if (selectedProviderType === 'manual') providerLabel = 'Manual (.ics)';
    return providerLabel ? `${found.name} (${providerLabel})` : found.name;
  };
 
  // determine whether the setup is complete enough to show dashboard button
  const allDone =
    masterSaved &&
      (selectedProviderType === 'manual'
        ? manualUploaded
        : (selectedProviderType === 'google' ||
          selectedProviderType === 'microsoft' ||
          selectedProviderType === 'apple')
        ? calendarsSaved && eventsSynced
        : false);

 useEffect(() => {
  if (allDone) {
    // Always show completion popup after full setup (including step 4 sync)
    sessionStorage.removeItem("from_disconnect_page");
    setShowSuccessPopup(true);
  }
}, [allDone]);

  // When setup is done, we show a confirmation and let the user go to dashboard
 
  return (
  <main className="profile-screen" style={{ backgroundImage: "url(/images/profile-bg.png)" }}>
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-sm-12">
            <div className="logout-btn">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                }}
              >
                Logout
              </a>
            </div>
          </div>
        <div className="col-lg-8 col-md-10">
          
          <div className="logo-section">
              <img src="images/logo.svg" className="logo" />
          </div>
 
          <div className="card">
            <div className="card-body">
              <h1 className="profile-title">Set up your Calendar</h1>
              <p className="profile-subtext">Connect and configure your calendar to start syncing your events seamlessly.</p>
             
         
     <div className="calendar-setup">

 
      {/* // Step 1: master calendar  */}
      <div className="mt-3">
        <h3 className="subtitle3">Step 1: Select Your Master Calendar</h3>
        {masterError && <div className="alert alert-danger">{masterError}</div>}
        {loadingMaster ? (
          <p>Loading calendars...</p>
        ) : (
          <div className="calendar-setup__field mt-3">
            {/* <label htmlFor="master-calendar-select" className='mb-2'></label> */}
            <div className="select-wrapper">
            <select
              id="master-calendar-select"
              value={selectedMasterCalendar || ''}
              onChange={handleMasterChange}
              className={`form-select form_select custom-select`}
            >
              <option value="">Select a Calendar</option>
              {masterCalendars.map((c) => (
                <option key={c.uid} value={c.uid}>
                  {c.name}
                </option>
              ))}
            </select>
            </div>
            {masterCalendars.length === 0 && !masterError && (
              <small className="text-muted d-block mt-1">
                No calendars available in master list. Please contact admin.
              </small>
            )}
          </div>
        )}
        {selectedMasterCalendar && selectedMasterCalendar !== savedMasterCalendar && (
          <button
            className="btn-style1 btn2 mt-4"
            onClick={handleSaveMasterCalendar}
            disabled={savingMaster}
          >
            {savingMaster ? 'Saving...' : 'Save & Continue'}
          </button>
        )}
      </div>
 
      {/* // Step 2: connect or manual upload info  */}
      {masterSaved && selectedProviderType === 'google' && (
        <div className="mt-5">
          <h5 className="subtitle3">Step 2: Connect Your Google Account</h5>
          {!googleConnected ? (
            <button
              className="btn-setup mt-3"
              onClick={handleGoogleConnect}
              disabled={syncLoading}
            >
              <i className="bi bi-google"></i>{' '}Connect Google Account
            </button>
          ) : (
            <div className="alert alert-success mt-3 mb-0">Google calendar connected.</div>
          )}
        </div>
      )}
 
      {masterSaved && selectedProviderType === 'microsoft' && (
        <div className="mt-5">
          <h5 className="subtitle3">Step 2: Connect Your Microsoft Account</h5>
          {!microsoftConnected ? (
            <button
              className="btn-setup mt-3"
              onClick={handleMicrosoftConnect}
              disabled={syncLoading}
            >
              <i className="bi bi-windows"></i>{' '}Connect Microsoft Account
            </button>
          ) : (
            <div className="alert alert-success mt-3 mb-0">Microsoft calendar connected.</div>
          )}
        </div>
      )}

      {masterSaved && selectedProviderType === 'apple' && (
        <div className="mt-5">
          <h5 className="subtitle3">Step 2: Connect Your Apple Calendar</h5>
          {!appleConnected ? (
            <>
              {!showAppleForm ? (
                <button
                  className="btn-setup mt-3"
                  onClick={() => setShowAppleForm(true)}
                  disabled={syncLoading}
                >
                  <i className="bi bi-apple"></i>{' '}Connect Apple Calendar
                </button>
              ) : (
                <>
                  <div className="mb-3 mt-3">
                    <label className="form-label label_input">iCloud Email</label>
                    <input
                      type="email"
                      className="form-control input_type"
                      value={appleEmail}
                      onChange={(e) => setAppleEmail(e.target.value)}
                      placeholder="yourname@icloud.com"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label label_input">App-Specific Password</label>
                    <input
                      type="password"
                      className="form-control input_type"
                      value={appleAppPassword}
                      onChange={(e) => setAppleAppPassword(e.target.value)}
                      placeholder="xxxx-xxxx-xxxx-xxxx"
                    />
                    <small className="text-muted d-block mt-1">
                      Generate an app-specific password from your Apple ID security settings.
                    </small>
                  </div>
                  <button
                    className="btn-setup"
                    onClick={handleAppleConnect}
                    disabled={syncLoading || savingApple}
                  >
                    {savingApple ? 'Connecting...' : 'Connect Apple Calendar'}
                  </button>
                </>
              )}
            </>
          ) : (
            <div className="alert alert-success mt-3 mb-0">Apple calendar connected.</div>
          )}
        </div>
      )}
 
      {masterSaved && selectedProviderType === 'manual' && (
        <div className="mt-4">
          <div className="mb-3">
            <label className="form-label label_input">Upload Your Calendar File (.ics)</label>
            <input
              type="file"
              accept=".ics,text/calendar"
              className="form-control input_type"
              onChange={handleManualFileChange}
            />
          </div>
          <button
            type="button"
            className="btn-setup"
            onClick={handleManualUpload}
            disabled={manualUploading}
          >
            {manualUploading ? 'Uploading...' : 'Upload Calendar File'}
          </button>
        </div>
      )}
 
      {error && masterSaved && selectedProviderType !== 'manual' && (
        <div className="alert alert-danger mt-3">{error}</div>
      )}
      {syncSuccessMessage && (
        <div className="alert alert-success mt-3">{syncSuccessMessage}</div>
      )}
 
    {/* //   Step 3: choose provider calendar (Google/Microsoft)  */}
      {selectedProviderType === 'google' && googleConnected && googleCalendars.length > 0 && (
        <div className="mt-4">
          <h5 className="subtitle3">Step 3: Choose a Google Calendar to Sync</h5>

          <div className="list-group">
            {googleCalendars.map((cal) => (
              <label
                key={cal.id}
                className="list-group-item d-flex align-items-center gap-2"
              >
                <input
                  type="radio"
                  name="google-calendar"
                  className="form-check-input"
                  checked={selectedGoogleId === cal.id}
                  onChange={() => toggleGoogleSelection(cal.id)}
                />
                <span>
                  {cal.name}
                </span>
              </label>
            ))}
          </div>
          {selectedGoogleId && selectedGoogleId !== savedGoogleId && (
            <button
              className="btn-setup mt-3"
              onClick={handleSaveGoogleCalendars}
              disabled={savingGoogle}
            >
              {savingGoogle ? 'Saving...' : 'Save Selection'}
            </button>
          )}
        </div>
      )}
 
      {selectedProviderType === 'microsoft' && microsoftConnected && microsoftCalendars.length > 0 && (
        <div className="mt-4">
          <h5 className="subtitle4">Step 3: Choose a Microsoft Calendar to Sync</h5>
          <div className="list-group mt-3">
            {microsoftCalendars.map((cal) => (
              <label
                key={cal.id}
                className="list-group-item d-flex align-items-center gap-2"
              >
                <input
                  type="radio"
                  name="microsoft-calendar"
                  className="form-check-input"
                  checked={selectedMicrosoftId === cal.id}
                  onChange={() => toggleMicrosoftSelection(cal.id)}
                />
                <span>
                  {cal.name}
                </span>
              </label>
            ))}
          </div>
          {selectedMicrosoftId && selectedMicrosoftId !== savedMicrosoftId && (
            <button
              className="btn-setup mt-3"
              onClick={handleSaveMicrosoftCalendars}
              disabled={savingMicrosoft}
            >
              {savingMicrosoft ? 'Saving...' : 'Save Selection'}
            </button>
          )}
        </div>
      )}

      {selectedProviderType === 'apple' && appleConnected && appleCalendars.length > 0 && (
        <div className="mt-4">
          <h5 className="subtitle3">Step 3: Choose an Apple Calendar to Sync</h5>
          <div className="list-group">
            {appleCalendars.map((cal) => (
              <label
                key={cal.id}
                className="list-group-item d-flex align-items-center gap-2"
              >
                <input
                  type="radio"
                  name="apple-calendar"
                  className="form-check-input"
                  checked={selectedAppleId === cal.id}
                  onChange={() => toggleAppleSelection(cal.id)}
                />
                <span>{cal.name}</span>
              </label>
            ))}
          </div>
          {selectedAppleId && selectedAppleId !== savedAppleId && (
            <button
              className="btn-setup mt-3"
              onClick={handleSaveAppleCalendars}
              disabled={savingApple}
            >
              {savingApple ? 'Saving...' : 'Save Selection'}
            </button>
          )}
        </div>
      )}
{/*
       Step 4: sync events for Google/Microsoft */}
      {calendarsSaved && selectedProviderType === 'google' && !eventsSynced && (
        <div className="mt-4">
          <h5 className="subtitle3">Step 4: Sync Google Events</h5>
          <button
            className="btn-setup mt-2"
            onClick={handleGoogleSyncEvents}
            disabled={syncLoading}
          >
            {syncLoading && syncProviderLabel === 'Google' ? 'Syncing...' : 'Sync Google Events'}
          </button>
        </div>
      )}
 
      {calendarsSaved && selectedProviderType === 'microsoft' && !eventsSynced && (
        <div className="mt-4">
          <h5 className="subtitle4">Step 4: Sync Microsoft Events</h5>
          <button
            className="btn-setup me-2 mt-2"
            onClick={handleMicrosoftSyncEvents}
            disabled={syncLoading}
          >
            {syncLoading && syncProviderLabel === 'Microsoft' ? 'Syncing...' : 'Sync Microsoft Events'}
          </button>
        </div>
      )}

      {calendarsSaved && selectedProviderType === 'apple' && !eventsSynced && (
        <div className="mt-4">
          <h5 className="subtitle3">Step 4: Sync Apple Events</h5>
          <button
            className="btn-setup mt-2"
            onClick={handleAppleSyncEvents}
            disabled={syncLoading}
          >
            {syncLoading && syncProviderLabel === 'Apple' ? 'Syncing...' : 'Sync Apple Events'}
          </button>
        </div>
      )}
     
    </div>
 
    </div>
          </div>
        </div>
      </div>
    </div>
 {/* Final: show success popup and go to dashboard on user click */}
     {showSuccessPopup && (

  <div
    className="modal fade show d-block"
    id="exampleModal"
    tabIndex="-1"
    role="dialog"
    aria-modal="true"
    style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
  >
    <div className="modal-dialog modal-dialog-centered modal-lg">
      <div className="modal-content profile-modal">
        <div className="modal-body">
          <div className="success-icon">
            <img src="/images/setup.svg" alt="success" />
          </div>

          <h4 className="mt-3 profile-modal-title">Profile Ready</h4>

          <p className="profile-text">
            Your profile has been set up successfully.
          </p>

          <button
            className="modal-btn mt-3"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  </div>
)}

  </main>
  );
};
 
export default CalendarSetup;
